<template>
  <section class="ead-page avaliacao-page">
    <titlebar :title-stack="['Avaliação final']" />
    <header class="box ead-hero">
      <div>
        <p class="ead-eyebrow">Última etapa do curso</p>
        <h1 class="title is-4 mb-2">Avaliação final</h1>
        <p class="ead-lead">Responda todas as perguntas para registrar sua tentativa. Sua melhor nota será mantida.</p>
      </div>
      <button type="button" class="button is-light" @click="$router.back()">
        <b-icon icon="arrow-left" size="is-small" /><span>Voltar ao curso</span>
      </button>
    </header>

    <div v-if="!tentativa" class="box introducao-card">
      <div v-if="resultado" class="resultado-card" :class="resultado.aprovado ? 'resultado-card--aprovado' : 'resultado-card--pendente'" role="status">
        <b-icon :icon="resultado.aprovado ? 'check-circle-outline' : 'information-outline'" size="is-medium" />
        <div>
          <h2 class="title is-5 mb-1">{{ resultado.aprovado ? 'Avaliação concluída' : 'Continue praticando' }}</h2>
          <p>Nota desta tentativa: <strong>{{ resultado.nota }}</strong> · Melhor nota: <strong>{{ resultado.melhorNota }}</strong></p>
        </div>
      </div>
      <div class="introducao-conteudo">
        <span class="introducao-icone"><b-icon icon="clipboard-check-outline" size="is-medium" /></span>
        <h2 class="title is-5 mb-2">{{ resultado ? 'Pronto para tentar novamente?' : 'Pronto para começar?' }}</h2>
        <p class="ead-lead mb-4">Não há limite de tentativas. Reserve um momento para ler cada questão com atenção.</p>
        <b-button type="is-primary" icon-left="play" :loading="loading" @click="iniciar">
          {{ resultado ? 'Nova tentativa' : 'Iniciar avaliação' }}
        </b-button>
      </div>
    </div>

    <form v-else class="box questoes-card" @submit.prevent="finalizar">
      <div class="questoes-cabecalho">
        <div>
          <p class="ead-eyebrow">Tentativa em andamento</p>
          <h2 class="title is-4 mb-1">{{ tentativa.nome }}</h2>
          <p class="ead-lead">{{ respondidas }} de {{ tentativa.perguntas.length }} perguntas respondidas</p>
        </div>
        <span class="questoes-contador">{{ Math.round((respondidas / tentativa.perguntas.length) * 100) || 0 }}%</span>
      </div>
      <b-progress :value="respondidas" :max="tentativa.perguntas.length" type="is-primary" />
      <fieldset v-for="(p, index) in tentativa.perguntas" :key="p.id" class="questao">
        <legend><span class="questao-numero">{{ index + 1 }}</span>{{ p.enunciado }}</legend>
        <div v-for="a in p.alternativas" :key="a.id" class="alternativa" :class="{ 'alternativa--selecionada': respostas[p.id] === a.id }">
          <b-radio v-model="respostas[p.id]" :native-value="a.id">{{ a.descricao }}</b-radio>
        </div>
      </fieldset>
      <div class="questoes-rodape">
        <span>Confira suas respostas antes de finalizar.</span>
        <b-button native-type="submit" type="is-primary" icon-left="check" :loading="loading">Finalizar tentativa</b-button>
      </div>
    </form>
  </section>
</template>

<script>
import http from '@/http'
import titlebar from '@/components/titlebar.vue'

export default {
  name: 'EadAvaliacao',
  components: { titlebar },
  data: () => ({ tentativa: null, respostas: {}, resultado: null, loading: false }),
  computed: {
    respondidas() { return Object.values(this.respostas).filter(Boolean).length },
  },
  methods: {
    iniciar() {
      this.loading = true
      http.post(`/api/ead-avaliacoes/${this.$route.params.id}/tentativas`)
        .then((r) => {
          this.tentativa = r.data
          this.respostas = {}
          r.data.perguntas.forEach((p) => this.$set(this.respostas, p.id, null))
        })
        .finally(() => { this.loading = false })
    },
    finalizar() {
      if (this.respondidas !== this.tentativa.perguntas.length) {
        this.$buefy.toast.open({ message: 'Responda todas as questões.', type: 'is-warning' })
        return
      }
      this.loading = true
      const respostas = Object.keys(this.respostas).map((id) => ({
        perguntaId: Number(id), alternativaId: this.respostas[id],
      }))
      http.put(`/api/ead-avaliacoes/tentativas/${this.tentativa.id}/finalizar`, { respostas })
        .then((r) => { this.resultado = r.data; this.tentativa = null })
        .finally(() => { this.loading = false })
    },
  },
}
</script>

<style scoped>
.avaliacao-page { max-width: 1080px; }
.introducao-card { padding: 1.75rem; }
.introducao-conteudo { padding: 1.6rem 1rem; text-align: center; }
.introducao-conteudo .ead-lead { margin-left: auto; margin-right: auto; }
.introducao-icone { align-items: center; background: #e8f5f1; border-radius: 18px; color: #008f7c; display: inline-flex; height: 4rem; justify-content: center; margin-bottom: 1rem; width: 4rem; }
.resultado-card { align-items: center; border-radius: 12px; display: flex; gap: 1rem; padding: 1rem 1.2rem; }
.resultado-card--aprovado { background: #eaf7ed; color: #267247; }
.resultado-card--pendente { background: #fff6e5; color: #8a610e; }
.resultado-card .title { color: inherit; }
.questoes-card { padding: 1.75rem; }
.questoes-cabecalho { align-items: center; display: flex; justify-content: space-between; margin-bottom: 1rem; }
.questoes-contador { background: #e8f5f1; border-radius: 999px; color: #087462; font-weight: 800; padding: 0.5rem 0.85rem; }
.questao { border: 0; border-top: 1px solid #e4eeeb; margin: 1.5rem 0 0; padding: 1.5rem 0 0; }
.questao legend { align-items: center; display: flex; font-weight: 700; gap: 0.75rem; margin-bottom: 1rem; padding-top: 1.5rem; width: 100%; }
.questao-numero { align-items: center; background: #e8f5f1; border-radius: 9px; color: #087462; display: inline-flex; flex: 0 0 2rem; height: 2rem; justify-content: center; }
.alternativa { border: 1px solid #dce8e5; border-radius: 10px; cursor: pointer; display: block; margin: 0.55rem 0; padding: 0.8rem 1rem; transition: border-color 150ms ease, background 150ms ease; }
.alternativa:hover, .alternativa--selecionada { background: #f1faf7; border-color: #00a68e; }
.questoes-rodape { align-items: center; border-top: 1px solid #e4eeeb; color: #63787b; display: flex; gap: 1rem; justify-content: space-between; margin-top: 1.75rem; padding-top: 1.5rem; }
@media screen and (max-width: 768px) { .questoes-card { padding: 1rem; } .questoes-rodape { align-items: stretch; flex-direction: column; } }
</style>
